import { useState } from 'react';
import type { Chat, Message } from '../data/mock';
import { directory } from '../data/mock';
import { usePersonLookup } from '../data/people';
import { Icon } from '../components/Icon';
import {
  Avatar,
  Badge,
  Button,
  Chip,
  ExternalLabel,
  Fab,
  IconButton,
  ListRow,
  NavBar,
  OrgLogo,
  PersonAvatar,
  SearchField,
  cx,
} from '../components/ui';
import {
  acceptRequest,
  chatList,
  declineRequest,
  openDetail,
  pop,
  push,
  useStore,
} from '../state/store';
import { useActiveTheme } from '../state/theme';
import { useWide } from '../shell/adaptive';

type Filter = 'all' | 'unread' | 'groups';

export function chatTitle(chat: Chat, lookup: ReturnType<typeof usePersonLookup>): string {
  if (chat.kind === 'group') return chat.name ?? 'Group';
  const other = chat.members.find((m) => m !== 'me') ?? 'me';
  return lookup(other).name;
}

function preview(
  m: Message | undefined,
  lookup: ReturnType<typeof usePersonLookup>,
  group: boolean,
): string {
  if (!m) return '';
  const who =
    group && m.from !== 'me'
      ? `${lookup(m.from).name.split(' ')[0]}: `
      : m.from === 'me'
        ? 'You: '
        : '';
  switch (m.kind) {
    case 'image':
      return `${who}📷 ${m.text ?? 'Photo'}`;
    case 'document':
      return `${who}📄 ${m.doc?.name}`;
    case 'voice':
      return `${who}🎤 Voice message (0:${String(m.voice?.seconds ?? 0).padStart(2, '0')})`;
    case 'call':
      return m.text ?? 'Call';
    default:
      return `${who}${m.text ?? ''}`;
  }
}

export function ChatList() {
  const chats = useStore(chatList);
  const requests = useStore((s) => s.requests);
  const hasOrg = useStore((s) => s.orgId !== null);
  const selected = useStore((s) => {
    const top = s.stacks.chats[s.stacks.chats.length - 1];
    return top?.name === 'chat' ? top.id : top?.name === 'requests' ? 'requests' : undefined;
  });
  const lookup = usePersonLookup();
  const theme = useActiveTheme();
  const wide = useWide();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');

  const visible = chats
    .filter((c) =>
      filter === 'unread' ? (c.unread ?? 0) > 0 : filter === 'groups' ? c.kind === 'group' : true,
    )
    .filter((c) => chatTitle(c, lookup).toLowerCase().includes(query.toLowerCase()))
    .sort((a, b) => Number(!!b.pinned) - Number(!!a.pinned));

  const open = (route: Parameters<typeof openDetail>[0]) =>
    wide ? openDetail(route, 'chats') : push(route, 'chats');

  return (
    <div className="screen">
      <NavBar
        large
        title={
          <span className="title-with-logo">
            {theme.orgName && <OrgLogo size={28} />}
            Chats
          </span>
        }
        trailing={
          wide && hasOrg ? (
            <IconButton icon="compose" label="New chat" onClick={() => open({ name: 'newChat' })} />
          ) : undefined
        }
      />
      <div className="screen-scroll">
        <div className="list-header">
          <SearchField value={query} onChange={setQuery} placeholder="Search chats and messages" />
          <div className="chip-row">
            <Chip selected={filter === 'all'} onClick={() => setFilter('all')}>
              All
            </Chip>
            <Chip selected={filter === 'unread'} onClick={() => setFilter('unread')}>
              Unread
            </Chip>
            <Chip selected={filter === 'groups'} onClick={() => setFilter('groups')}>
              Groups
            </Chip>
          </div>
        </div>

        {requests.length > 0 && (
          <button
            type="button"
            className={cx('request-banner', selected === 'requests' && 'is-selected')}
            onClick={() => open({ name: 'requests' })}
          >
            <span className="request-banner-icon">
              <Icon name="request" size={20} />
            </span>
            <span className="list-row-text">
              <span className="list-row-title">Chat requests</span>
              <span className="list-row-subtitle">
                {requests.map((r) => lookup(r.from).name.split(' ')[0]).join(', ')} · from outside
                your organisation
              </span>
            </span>
            <Badge count={requests.length} />
            <Icon name="chevronRight" size={18} className="list-row-chevron" />
          </button>
        )}

        <div className="chat-rows">
          {visible.map((c) => {
            const last = c.messages[c.messages.length - 1];
            const other =
              c.kind === 'direct' ? lookup(c.members.find((m) => m !== 'me') ?? 'me') : undefined;
            return (
              <button
                type="button"
                key={c.id}
                className={cx('chat-row', selected === c.id && 'is-selected')}
                onClick={() => open({ name: 'chat', id: c.id })}
              >
                {other ? (
                  <PersonAvatar id={other.id} size={50} />
                ) : (
                  <Avatar name={c.name ?? ''} hue={c.avatar ?? 'blue'} size={50} group />
                )}
                <span className="chat-row-main">
                  <span className="chat-row-top">
                    <span className="chat-row-name">
                      {chatTitle(c, lookup)}
                      {other?.external && <ExternalLabel org={other.external} />}
                    </span>
                    <span
                      className={cx(
                        'chat-row-time',
                        (c.unread ?? 0) > 0 && !c.muted && 'is-unread',
                      )}
                    >
                      {last?.time}
                    </span>
                  </span>
                  <span className="chat-row-bottom">
                    <span className="chat-row-preview">
                      {c.typing ? (
                        <em className="typing-text">typing…</em>
                      ) : (
                        <>
                          {last?.from === 'me' && last.status && (
                            <Icon
                              name={
                                last.status === 'sent' || last.status === 'sending'
                                  ? 'tickSent'
                                  : 'tickDelivered'
                              }
                              size={16}
                              className={cx('tick', last.status === 'read' && 'is-read')}
                            />
                          )}
                          {preview(last, lookup, c.kind === 'group')}
                        </>
                      )}
                    </span>
                    <span className="chat-row-flags">
                      {c.liveCall && (
                        <span className="live-pill">
                          <Icon name="video" size={12} /> Live
                        </span>
                      )}
                      {c.muted && <Icon name="mute" size={15} className="flag-icon" />}
                      {c.pinned && !(c.unread ?? 0) && (
                        <Icon name="pin" size={15} className="flag-icon" />
                      )}
                      <Badge count={c.unread ?? 0} muted={c.muted} />
                    </span>
                  </span>
                </span>
              </button>
            );
          })}
        </div>
        {!hasOrg && (
          <p className="list-footnote">
            <Icon name="lock" size={13} /> You can chat with people who invite you. Chats are
            end-to-end encrypted.
          </p>
        )}
        {hasOrg && (
          <p className="list-footnote">
            <Icon name="lock" size={13} /> Your personal messages are end-to-end encrypted.
          </p>
        )}
      </div>
      {!wide && hasOrg && (
        <Fab icon="add" label="New chat" onClick={() => open({ name: 'newChat' })} />
      )}
    </div>
  );
}

export function ChatRequests() {
  const requests = useStore((s) => s.requests);
  const lookup = usePersonLookup();
  const wide = useWide();
  return (
    <div className="screen">
      <NavBar title="Chat requests" onBack={wide ? undefined : pop} center={!wide} />
      <div className="screen-scroll">
        <p className="screen-intro">
          People outside Nordwind Logistik who found you by email. They can’t see whether you’ve
          read their message until you accept.
        </p>
        {requests.length === 0 && <p className="screen-intro">No open requests.</p>}
        {requests.map((r) => {
          const p = lookup(r.from);
          return (
            <article key={r.id} className="request-card">
              <div className="request-head">
                <PersonAvatar id={p.id} size={44} />
                <div className="list-row-text">
                  <span className="list-row-title">
                    {p.name} {p.external && <ExternalLabel org={p.external} />}
                  </span>
                  <span className="list-row-subtitle">
                    {r.via} · {r.time}
                  </span>
                </div>
              </div>
              <p className="request-preview">{r.preview}</p>
              <div className="request-actions">
                <Button variant="secondary" onClick={() => declineRequest(r.id)}>
                  Decline
                </Button>
                <Button onClick={() => acceptRequest(r.id)}>Accept</Button>
              </div>
              <button type="button" className="text-link danger">
                Block and report
              </button>
            </article>
          );
        })}
      </div>
    </div>
  );
}

export function NewChat() {
  const lookup = usePersonLookup();
  const chats = useStore(chatList);
  const wide = useWide();
  const [query, setQuery] = useState('');
  const openWith = (id: string) => {
    const existing = chats.find((c) => c.kind === 'direct' && c.members.includes(id));
    if (existing) openDetail({ name: 'chat', id: existing.id }, 'chats');
  };
  const emailLike = query.includes('@');
  return (
    <div className="screen">
      <NavBar title="New chat" onBack={wide ? undefined : pop} center={!wide} />
      <div className="screen-scroll">
        <div className="list-header">
          <SearchField
            value={query}
            onChange={setQuery}
            placeholder="Name, or email for people outside"
          />
        </div>
        <div className="section-body flush">
          <ListRow icon="group" title="New group" chevron onClick={() => undefined} />
          <ListRow
            icon="mail"
            title="Invite by email"
            subtitle="People outside your organisation"
            chevron
          />
        </div>
        {emailLike && (
          <div className="section-body flush">
            <ListRow
              icon="request"
              title={`Send a chat request to ${query}`}
              subtitle="They’ll see your name and company, and can accept or decline"
            />
          </div>
        )}
        <h2 className="section-title inset">Nordwind Logistik</h2>
        <div className="section-body flush">
          {directory
            .map(lookup)
            .filter((p) => p.name.toLowerCase().includes(query.toLowerCase()))
            .map((p) => (
              <ListRow
                key={p.id}
                leading={<PersonAvatar id={p.id} size={40} />}
                title={p.name}
                subtitle={p.title}
                onClick={() => openWith(p.id)}
              />
            ))}
        </div>
      </div>
    </div>
  );
}
